import React, { useState } from "react";
import {
  Alert,
  Table,
  TableBody,
  TableCell,
  TableSortLabel,
  TableHead,
  TableRow,
  Box,
  Collapse,
  TextField,
  Button,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  TablePagination,
  useTheme,
  Menu,
} from "@mui/material";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import useAuditoriaSistema from "./useAuditoriaSistema";
import toast from "react-hot-toast";
import * as XLSX from "xlsx";

const colunas = [
  { value: "tabela", label: "Tabela" },
  { value: "id", label: "ID" },
  { value: "nome", label: "Nome" },
  { value: "aud_criado_por", label: "Criado por" },
  { value: "aud_criado_data", label: "Data de Criação" },
  { value: "aud_modificado_por", label: "Modificado por" },
  { value: "aud_modificado_data", label: "Data de Modificação" },
];

interface TabelaModificacoesSistemaProps {
  isOpen: boolean;
  onToggle: () => void;
}

interface AuditoriaRow {
  tabela: string;
  id: number;
  nome: string;
  aud_criado_por: string;
  aud_criado_data: string;
  aud_modificado_por: string;
  aud_modificado_data: string;
}

export default function TabelaModificacoesSistema({ isOpen, onToggle }: TabelaModificacoesSistemaProps) {
  const [isExporting, setIsExporting] = useState(false);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const theme = useTheme();
  const {
    data,
    isLoading,
    error,
    totalRecords,
    hasMore,
    colunaSelecionada,
    setColunaSelecionada,
    filtro,
    setFiltro,
    page,
    rowsPerPage,
    order,
    orderBy,
    isValidSearch,
    handleSearch,
    handleRequestSort,
    clearFilters,
    handleChangePage,
    handleChangeRowsPerPage,
    getAuditoriaExport,
  } = useAuditoriaSistema<AuditoriaRow>("modificacoes", isOpen, 25);
  const rowsData = data?.data ?? [];

  function handleClick(event: React.MouseEvent<HTMLButtonElement>) {
    setAnchorEl(event.currentTarget);
  }

  function handleClose() {
    setAnchorEl(null);
  }

  async function exportConsult(format: string) {
    setIsExporting(true);
    handleClose();

    try {
      const auditoriaExport = await getAuditoriaExport();

      if (!auditoriaExport || auditoriaExport.length < 1) {
        toast("Nenhum item encontrado para o filtro aplicado!");
        setIsExporting(false);
        return;
      }

      if (format === "csv") {
        exportToCSV(auditoriaExport);
        toast.success("Consulta exportada com sucesso!");
      } else if (format === "xlsx") {
        exportToXLSX(auditoriaExport);
        toast.success("Consulta exportada com sucesso!");
      } else {
        toast.error("Formato não suportado!");
      }
    } catch (error) {
      console.error(error);
      toast.error("Um erro inesperado aconteceu, tente novamente!");
    } finally {
      setIsExporting(false);
    }
  }

  function exportToCSV(data: Record<string, any>) {
    const expandedData = data.map((row) => {
      const flattenedRow: Record<string, any> = {};
      Object.keys(row).forEach((key) => {
        if (key !== "total_records") {
          if (typeof row[key] === "object" && row[key] !== null) {
            Object.keys(row[key]).forEach((subKey) => {
              if (row[key][subKey] !== null) {
                flattenedRow[`${key}_${subKey}`] = row[key][subKey];
              }
            });
          } else if (row[key] !== null) {
            flattenedRow[key] = row[key];
          }
        }
      });
      return flattenedRow;
    });

    const datePattern = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}(.\d{1,6})?)?$/;

    expandedData.forEach((row) => {
      Object.keys(row).forEach((key) => {
        const value = row[key];
        if (typeof value === "string" && datePattern.test(value)) {
          const date = new Date(value);
          row[key] = `${date.getDate().toString().padStart(2, "0")}/${(date.getMonth() + 1)
            .toString()
            .padStart(2, "0")}/${date.getFullYear()} ${date.getHours().toString().padStart(2, "0")}:${date
            .getMinutes()
            .toString()
            .padStart(2, "0")}:${date.getSeconds().toString().padStart(2, "0")}`;
        }
      });
    });

    const allHeaders = new Set<string>();
    expandedData.forEach((row) => {
      Object.keys(row).forEach((key) => allHeaders.add(key));
    });
    const headers = Array.from(allHeaders);

    const csvRows = expandedData.map((row) =>
      headers
        .map((header) => {
          const cellValue = row[header];
          return typeof cellValue === "string" ? `"${cellValue.replace(/"/g, '""')}"` : cellValue;
        })
        .join(","),
    );

    const csvContent = [`"${headers.join('","')}"`, ...csvRows].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", "auditoria_export.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  }

  function exportToXLSX(data: Record<string, any>) {
    const expandedData = data.map((row) => {
      const flattenedRow: any = {};
      Object.keys(row).forEach((key) => {
        if (key !== "total_records") {
          if (typeof row[key] === "object" && row[key] !== null) {
            Object.keys(row[key]).forEach((subKey) => {
              if (row[key][subKey] !== null) {
                flattenedRow[`${key}_${subKey}`] = row[key][subKey];
              }
            });
          } else if (row[key] !== null) {
            flattenedRow[key] = row[key];
          }
        }
      });
      return flattenedRow;
    });

    const datePattern = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}(.\d{1,6})?)?$/;

    expandedData.forEach((row) => {
      Object.keys(row).forEach((key) => {
        const value = row[key];
        if (typeof value === "string" && datePattern.test(value)) {
          const date = new Date(value);
          row[key] = `${date.getDate().toString().padStart(2, "0")}/${(date.getMonth() + 1)
            .toString()
            .padStart(2, "0")}/${date.getFullYear()} ${date.getHours().toString().padStart(2, "0")}:${date
            .getMinutes()
            .toString()
            .padStart(2, "0")}:${date.getSeconds().toString().padStart(2, "0")}`;
        }
      });
    });

    const worksheet = XLSX.utils.json_to_sheet(expandedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "AuditoriaExport");

    XLSX.writeFile(workbook, "auditoria_export.xlsx");
  }

  return (
    <Box width="100%">
      <Box
        onClick={onToggle}
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          cursor: "pointer",
          padding: "16px 24px",
          borderRadius: "0px",
          borderBottom: 1,
          borderColor: theme.palette.divider,
          marginLeft: theme.spacing(4),
          marginRight: theme.spacing(4),
          position: "relative",
        }}
      >
        <Box sx={{ fontWeight: "bold", fontSize: 18 }}>Modificações no sistema e em tabelas genéricas</Box>
        <Box
          sx={{
            position: "absolute",
            left: "600px",
            top: "50%",
            transform: "translateY(-50%)",
            fontSize: 14,
          }}
        >
          Veja todas as criações e modificações de dados no sistema
        </Box>
        {isOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
      </Box>

      <Collapse in={isOpen} timeout="auto" unmountOnExit>
        <Box sx={{ width: "100%", overflowX: "auto", padding: "16px" }}>
          {error && <Alert severity="error">Erro ao carregar os dados: {error.message}</Alert>}
          {isLoading ? (
            <Box display="flex" justifyContent="center" padding={2}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Box marginBottom={3} display="flex" gap={2} flexWrap="wrap" alignItems="center">
                <FormControl variant="outlined" sx={{ minWidth: 200 }}>
                  <InputLabel id="select-coluna">Coluna</InputLabel>
                  <Select
                    labelId="select-coluna"
                    value={colunaSelecionada}
                    onChange={(e) => setColunaSelecionada(e.target.value)}
                    label="Coluna"
                  >
                    {colunas.map((coluna) => (
                      <MenuItem key={coluna.value} value={coluna.value}>
                        {coluna.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  label="Filtro"
                  variant="outlined"
                  value={filtro}
                  onChange={(e) => setFiltro(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  sx={{ flex: 1, padding: "8px 0", minWidth: 200 }}
                />

                <Button
                  color="success"
                  onClick={handleSearch}
                  sx={{ width: "143.7px", height: "56px" }}
                  variant="contained"
                  disabled={!isValidSearch()}
                >
                  Filtrar
                </Button>

                <Button
                  color="warning"
                  onClick={clearFilters}
                  sx={{ width: "143.7px", height: "56px" }}
                  variant="contained"
                >
                  Limpar Filtros
                </Button>

                <Button
                  color="secondary"
                  onClick={handleClick}
                  sx={{ height: "56px" }}
                  variant="contained"
                  disabled={isExporting}
                >
                  {!isExporting ? "Exportar Consulta" : "Carregando..."}
                </Button>
                <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
                  <MenuItem onClick={() => exportConsult("xlsx")}>XLSX</MenuItem>
                  <MenuItem onClick={() => exportConsult("csv")}>CSV</MenuItem>
                </Menu>
              </Box>

              <Table sx={{ minWidth: 750, borderRadius: "8px" }}>
                <TableHead>
                  <TableRow>
                    {colunas.map((coluna) => (
                      <TableCell key={coluna.value} sx={{ padding: "16px" }}>
                        <TableSortLabel
                          active={orderBy === coluna.value}
                          direction={orderBy === coluna.value ? order : "asc"}
                          onClick={() => handleRequestSort(coluna.value)}
                        >
                          {coluna.label}
                        </TableSortLabel>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {!isLoading && !error && rowsData.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={colunas.length}>Nenhum dado encontrado</TableCell>
                    </TableRow>
                  )}
                  {rowsData.map((row) => (
                    <TableRow key={`${row.tabela}-${row.id}`}>
                      {colunas.map((coluna) => (
                        <TableCell key={coluna.value}>{row[coluna.value] ?? ""}</TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}

          <TablePagination
            component="div"
            labelRowsPerPage={"Linhas por página"}
            count={totalRecords}
            nextIconButtonProps={{ disabled: isLoading || !hasMore }}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={(event, newPage) => handleChangePage(event, newPage)}
            onRowsPerPageChange={(event) => handleChangeRowsPerPage(event)}
            rowsPerPageOptions={[10, 25, 50, 100]}
            sx={{ marginTop: 2 }}
          />
        </Box>
      </Collapse>
    </Box>
  );
}
